import React from "react";
import { capitalize, FormControl, FormHelperText, InputLabel, MenuItem, Checkbox } from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useFormikContext } from "formik";
import trim from "lodash/trim";
import { css } from "@emotion/react";
import { styled } from "@mui/material/styles";
import FormikMultiSelectValueRenderer from "./FormikMultiSelectValueRenderer";

const nanoid = require("nanoid");

type Props = {
  label: string;
  fieldName: string;
  disabled?: boolean;
  required?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: (evt: SelectChangeEvent<unknown>) => void;
  items: { value: string | number; displayName: string }[];
  multiple?: boolean;
  placeholder?: string;
};

const FormikSelect: React.FC<Props> = ({
  label,
  fieldName,
  disabled = false,
  required = false,
  multiple = false,
  onBlur,
  onChange,
  items,
  placeholder = "",
}) => {
  const {
    touched,
    errors,
    handleChange: formikHandleChange,
    handleBlur: formikHandleBlur,
    setFieldValue,
    values,
  } = useFormikContext<any>();

  const inputId = React.useMemo(() => fieldName + "-" + nanoid(5), [fieldName]);
  const hasError = touched[fieldName] && Boolean(errors[fieldName]);
  const handleBlur = React.useCallback(
    (evt: React.FocusEvent<HTMLInputElement>) => {
      // Trim text input
      if (!multiple) {
        setFieldValue(fieldName, trim(evt.target.value));
      }

      // Custom blur handler
      if (onBlur) {
        onBlur(evt);
      }

      // General blur handler
      formikHandleBlur(evt);
    },
    [formikHandleBlur, onBlur, setFieldValue, fieldName, multiple]
  );

  const handleChange = React.useCallback(
    (evt: SelectChangeEvent<unknown>) => {
      // General change handler (updates Formik value first)
      formikHandleChange(evt);

      // Custom change handler
      if (onChange) {
        onChange(evt);
      }
    },
    [formikHandleChange, onChange]
  );

  const renderValue = React.useCallback(
    (selected) => <FormikMultiSelectValueRenderer selected={selected} items={items} />,
    [items]
  );

  return (
    <FormControl
      fullWidth={true}
      error={hasError}
      hiddenLabel={true}
      required={required}
      disabled={disabled}
      variant={"outlined"}
    >
      <InputLabel htmlFor={inputId} shrink={false}>
        {label}
      </InputLabel>

      <StyledSelect
        data-testid={`FormikSelect-${fieldName}`}
        id={inputId}
        name={fieldName}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values[fieldName]}
        error={hasError}
        disabled={disabled}
        multiple={multiple}
        placeholder={placeholder}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 500,
            },
          },
        }}
        {...(multiple ? { renderValue } : {})}
      >
        {items.map((item) => (
          <StyledMenuItem value={item.value} key={item.value}>
            {multiple && <StyledCheckbox checked={values[fieldName].includes(item.value)} />}
            {item.displayName}
          </StyledMenuItem>
        ))}
      </StyledSelect>

      {hasError && (
        <FormHelperText error={true} data-testid={`InputError${capitalize(fieldName)}`}>
          {errors[fieldName]}
        </FormHelperText>
      )}
    </FormControl>
  );
};

const StyledMenuItem = styled(MenuItem)(
  ({ theme }) => css`
    gap: ${theme.spacing(2)};
  `
);

const StyledCheckbox = styled(Checkbox)(
  ({ theme }) => css`
    padding: ${theme.spacing(1.5)};
  `
);

const StyledSelect = styled(Select)(
  ({ theme, multiple }) => css`
    ${multiple &&
    css`
      .MuiSelect-select {
        padding: 8px 40px 8px 12px !important;
      }
    `}
  `
);

export default FormikSelect;
